import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { getServerTimeApi } from "../../Api/api";
import logo from "../../Assets/dbskillslogo.png";

// ─── Icons ──────────────────────────────────────────────────────────────
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconTime = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7B8DFF" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7B8DFF" strokeWidth="2" strokeLinecap="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconCamera = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const IconFlip = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/>
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
  </svg>
);
const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconRetake = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="1 4 1 10 7 10"/>
    <path d="M3.51 15a9 9 0 1 0 .49-4.95"/>
  </svg>
);
const IconDownload = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const IconShare = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

export default function CheckIn() {
  const navigate = useNavigate();
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [photo,              setPhoto]              = useState(null);   // base64 data URL
  const [photoBlob,          setPhotoBlob]          = useState(null);   // Blob for upload
  const [location,           setLocation]           = useState(null);
  const [address,            setAddress]            = useState("");
  const [serverOffset,       setServerOffset]       = useState(0);
  const [nowDate,            setNowDate]            = useState(new Date());
  const [time,               setTime]               = useState("");
  const [capturedTime,       setCapturedTime]       = useState("");
  const [userName,           setUserName]           = useState("user");
  const [loading,            setLoading]            = useState(false);
  const [downloading,        setDownloading]        = useState(false);
  const [downloadState,      setDownloadState]      = useState("idle");
  const [downloadedFileName, setDownloadedFileName] = useState("");
  const [sharing,            setSharing]            = useState(false);
  const [facingFront,        setFacingFront]        = useState(true);
  const [camReady,           setCamReady]           = useState(false);
  const [camError,           setCamError]           = useState("");
  const [locError,           setLocError]           = useState("");
  const [flash,              setFlash]              = useState(false);

  const formattedDate = nowDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // ── Sync Server Time ──────────────────────────────────────────────
  const syncServerTime = useCallback(async () => {
    try {
      const res = await getServerTimeApi();
      if (res) {
        // Handle server time from API response
        const serverTime = res.serverTime || res.timestamp || res.datetime || new Date().toISOString();
        const serverMs = new Date(serverTime).getTime();
        const offset = serverMs - Date.now();
        setServerOffset(offset);
        const curr = new Date(Date.now() + offset);
        setNowDate(curr);
        setTime(curr.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      }
    } catch (e) {
      console.log("Server time sync error:", e);
    }
  }, []);

  useEffect(() => {
    syncServerTime();
  }, [syncServerTime]);

  // ── Live Server Clock ──────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      const curr = new Date(Date.now() + serverOffset);
      setNowDate(curr);
      setTime(curr.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [serverOffset]);

  // ── Profile ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await API.get("/user/profile");
        if (res.data && res.data.data) {
          setUserName(res.data.data.username || res.data.data.name || "user");
        }
      } catch (e) {
        console.log("Profile fetch error:", e);
      }
    })();
  }, []);

  // ── Reverse Geocode ────────────────────────────────────────────────
  const getReadableAddress = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      if (data && data.address) {
        const addr = data.address;
        const parts = [
          addr.suburb || addr.neighbourhood || addr.residential,
          addr.city || addr.town || addr.village || addr.county,
          addr.state,
          addr.postcode,
          addr.country,
        ].filter(Boolean);
        const formatted = Array.from(new Set(parts)).join(", ");
        setAddress(formatted || data.display_name);
      } else if (data && data.display_name) {
        setAddress(data.display_name);
      }
    } catch (err) {
      console.log("Reverse geocode error:", err);
    }
  }, []);

  // ── Location ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) { setLocError("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setLocation(coords);
        getReadableAddress(coords.latitude, coords.longitude);
      },
      () => setLocError("Location access denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [getReadableAddress]);

  // ── Camera ──────────────────────────────────────────────────────────
  const startCamera = useCallback(async (front = true) => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setCamReady(false);
    setCamError("");
    try {
      const constraints = {
        video: {
          facingMode: front ? "user" : "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCamReady(true);
      }
    } catch {
      setCamError("Camera access denied. Please allow camera in browser settings.");
    }
  }, []);

  useEffect(() => {
    startCamera(facingFront);
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, [facingFront, startCamera]);

  // ── Take photo with Server Time Watermark ──────────────────────────
  const takePhoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width  = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    const currentServerDate = new Date(Date.now() + serverOffset);
    const snapTime = currentServerDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const snapDate = currentServerDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    setCapturedTime(snapTime);

    // Mirror if front camera
    if (facingFront) {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // ── Watermark Overlay (Bottom 25%) ──
    const wmHeight = height * 0.24;
    const wmY = height - wmHeight;

    // Dark glass background box
    ctx.fillStyle = "rgba(8, 16, 36, 0.85)";
    ctx.fillRect(8, wmY - 8, width - 16, wmHeight);

    ctx.strokeStyle = "rgba(6, 182, 212, 0.4)";
    ctx.lineWidth = 2;
    ctx.strokeRect(8, wmY - 8, width - 16, wmHeight);

    // Brand Title
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 20px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("DB Skills", 26, wmY + 22);

    ctx.fillStyle = "rgba(6, 182, 212, 0.9)";
    ctx.font = "600 13px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("Attendance System", 120, wmY + 22);

    // Status & Server Time
    ctx.fillStyle = "#4ADE80";
    ctx.font = "bold 17px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("✓ CHECKED IN", width - 250, wmY + 22);

    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.font = "bold 17px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(snapTime, width - 95, wmY + 22);

    // Divider Line
    ctx.strokeStyle = "rgba(6, 182, 212, 0.25)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(26, wmY + 34);
    ctx.lineTo(width - 26, wmY + 34);
    ctx.stroke();

    // Date & Coordinates
    ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
    ctx.font = "500 15px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(`📅 ${snapDate}`, 26, wmY + 58);

    if (location) {
      const locStr = `📍 ${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
      ctx.fillText(locStr, width - 250, wmY + 58);
    }

    // Address
    if (address) {
      const shortAddr = address.length > 60 ? address.substring(0, 57) + "..." : address;
      ctx.fillText(`🏠 ${shortAddr}`, 26, wmY + 85);
    }

    // Shutter flash
    setFlash(true);
    setTimeout(() => setFlash(false), 200);

    canvas.toBlob((blob) => {
      setPhotoBlob(blob);
      setPhoto(canvas.toDataURL("image/jpeg", 0.9));
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    }, "image/jpeg", 0.9);
  };

  // ── Download Photo ──────────────────────────────────────────────────
  const downloadPhoto = () => {
    if (!photo) return;
    setDownloading(true);
    setDownloadState("downloading");

    setTimeout(() => {
      const currentServerDate = new Date(Date.now() + serverOffset);
      const cleanUser = String(userName || "user")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_");
      const datePart = currentServerDate.toISOString().split("T")[0];
      const timePart = currentServerDate.toTimeString().split(" ")[0].replace(/:/g, "-");
      const fileName = `${cleanUser}_checkin_${datePart}_${timePart}.jpg`;

      const link = document.createElement("a");
      link.href = photo;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadedFileName(fileName);
      setDownloadState("success");
      setDownloading(false);

      setTimeout(() => {
        setDownloadState("idle");
      }, 3500);
    }, 500);
  };

  // ── Share Photo ─────────────────────────────────────────────────────
  const sharePhoto = async () => {
    if (!photoBlob) return;
    try {
      setSharing(true);
      const currentServerDate = new Date(Date.now() + serverOffset);
      const cleanUser = String(userName || "user")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_");
      const datePart = currentServerDate.toISOString().split("T")[0];
      const timePart = currentServerDate.toTimeString().split(" ")[0].replace(/:/g, "-");
      const fileName = `${cleanUser}_checkin_${datePart}_${timePart}.jpg`;

      const file = new File([photoBlob], fileName, { type: "image/jpeg" });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Check In Photo",
          text: `Check In Photo for ${userName}`,
        });
      } else {
        downloadPhoto();
      }
    } catch (e) {
      console.log("Share error:", e);
    } finally {
      setSharing(false);
    }
  };

  // ── Retake ──────────────────────────────────────────────────────────
  const retake = () => {
    setPhoto(null);
    setPhotoBlob(null);
    setDownloadState("idle");
    startCamera(facingFront);
  };

  // ── Confirm check-in ────────────────────────────────────────────────
  const confirmCheckIn = async () => {
    if (!photo)    return alert("Please take a photo first");
    if (!location) return alert("Waiting for location — please try again");
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("photo",    new File([photoBlob], "checkin.jpg", { type: "image/jpeg" }));
      formData.append("location", JSON.stringify({ latitude: location.latitude, longitude: location.longitude }));
      const res = await API.post("/attendance/checkin", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        navigate("/dashboard", { replace: true });
      } else {
        alert(res.data.message || "Check-in failed");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  // ── Map embed URL ────────────────────────────────────────────────────
  const mapSrc = location
    ? `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=16&output=embed`
    : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .ci-frame {
          min-height: 100dvh;
          background: #000;
          display: flex; align-items: flex-start; justify-content: center;
          font-family: 'Plus Jakarta Sans', sans-serif;
          padding: 10px;
        }
        .ci-phone {
          width: 100%; max-width: 420px;
        }
        @media (min-width: 480px) {
          .ci-frame { align-items: center; padding: 20px; }
          .ci-phone { max-width: 440px; }
        }

        .ci-card {
          background: #fff;
          border-radius: 30px;
          padding: 52px 14px 14px;
          position: relative;
          overflow: hidden;
        }

        .ci-header {
          position: absolute; top: 12px; left: 14px; right: 14px;
          display: flex; justify-content: space-between; align-items: center;
          z-index: 20;
        }
        .ci-title-pill {
          background: #F2F2F2;
          padding: 6px 15px; border-radius: 20px;
          font-size: 14px; font-weight: 800; color: #111;
        }
        .ci-close {
          width: 35px; height: 35px; border-radius: 50%;
          background: #F2F2F2; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #333; transition: background 0.2s;
        }
        .ci-close:hover { background: #E5E7EB; }

        .ci-cam-box {
          border-radius: 20px; overflow: hidden;
          background: #111;
          aspect-ratio: 3/4;
          margin: 0 auto;
          max-height: 52vh;
          position: relative;
        }
        .ci-cam-box video,
        .ci-cam-box img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .ci-cam-box video { transform: scaleX(-1); }
        .ci-cam-flash {
          position: absolute; inset: 0;
          background: #fff; opacity: 0;
          pointer-events: none;
          transition: opacity 0.05s;
        }
        .ci-cam-flash.on { opacity: 0.7; }
        .ci-cam-error {
          position: absolute; inset: 0;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 10px; padding: 20px; text-align: center;
          color: #9CA3AF; font-size: 13px; background: #1a1a1a;
        }

        .ci-time-pill {
          position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
          background: #fff; border-radius: 25px;
          padding: 6px 14px;
          display: flex; align-items: center; gap: 5px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.25);
          white-space: nowrap;
          font-size: 13px; font-weight: 600; color: #111;
        }

        .ci-flip-btn {
          position: absolute; top: 12px; right: 12px;
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(0,0,0,0.45);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #fff; transition: background 0.2s;
        }
        .ci-flip-btn:hover { background: rgba(0,0,0,0.65); }

        /* Floating action buttons */
        .ci-floating-btns {
          position: absolute; top: 12px; right: 12px;
          display: flex; flex-direction: column; gap: 8px;
          z-index: 30;
        }
        .ci-float-btn {
          width: 40px; height: 40px; border-radius: 50%;
          background: rgba(0,0,0,0.65);
          border: 1px solid rgba(255,255,255,0.25);
          color: #fff; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.2s, background 0.2s;
        }
        .ci-float-btn:hover { transform: scale(1.08); background: rgba(0,0,0,0.8); }
        .ci-float-btn.success { background: #10B981; border-color: #34D399; }
        .ci-float-btn.share { background: rgba(6, 182, 212, 0.85); border-color: rgba(6, 182, 212, 0.4); }

        /* Watermark Overlay on screen */
        .wm-overlay-screen {
          position: absolute; bottom: 6px; left: 6px; right: 6px;
          background: rgba(8, 16, 36, 0.58);
          border: 1px solid rgba(6, 182, 212, 0.28);
          border-radius: 12px; padding: 7px 10px 6px;
          z-index: 25; pointer-events: none;
          backdrop-filter: blur(1px);
        }
        .wm-brand-row { display: flex; justify-content: space-between; align-items: center; }
        .wm-brand-left { display: flex; align-items: center; gap: 5px; }
        .wm-logo-wrap {
          width: 20px; height: 20px; border-radius: 4px;
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(6, 182, 212, 0.4);
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .wm-logo { width: 16px; height: 14px; object-fit: contain; }
        .wm-title-bold { color: #fff; font-weight: 800; font-size: 10px; line-height: 12px; letter-spacing: 0.2px; }
        .wm-title-sub { color: rgba(6, 182, 212, 0.9); font-size: 7.5px; font-weight: 600; line-height: 9px; letter-spacing: 0.3px; }
        .wm-status-in { color: #4ade80; font-weight: 800; font-size: 9.5px; letter-spacing: 0.5px; }
        .wm-time { color: rgba(255,255,255,0.9); font-weight: 700; font-size: 9.5px; margin-left: 4px; }
        .wm-separator { height: 1px; background: rgba(6, 182, 212, 0.25); margin: 3px 0; }
        .wm-meta-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
        .wm-meta-text { color: rgba(255, 255, 255, 0.85); font-size: 8.5px; font-weight: 500; line-height: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* Toast banner */
        .ci-toast-banner {
          position: absolute; top: 10px; left: 10px; right: 65px;
          background: rgba(10, 22, 40, 0.72);
          border: 1px solid rgba(16, 185, 129, 0.38);
          border-radius: 12px; padding: 6px 10px;
          display: flex; align-items: center; gap: 8px;
          z-index: 30; animation: slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes slideDown { from { transform: translateY(-30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .ci-toast-title { color: #fff; font-size: 11px; font-weight: 700; }
        .ci-toast-sub { color: #A7F3D0; font-size: 9px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .ci-loc-card {
          margin-top: 12px;
          background: #F7F7F7;
          border-radius: 20px;
          padding: 10px;
        }
        .ci-loc-header {
          display: flex; align-items: center; gap: 6px;
          margin-bottom: 8px;
          font-size: 13px; font-weight: 600; color: #111;
        }
        .ci-map-box {
          border-radius: 14px; overflow: hidden;
          height: 110px;
          background: #E5E7EB;
        }
        .ci-map-box iframe { width: 100%; height: 100%; border: none; display: block; }
        .ci-map-loading {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          color: #9CA3AF; font-size: 12px; gap: 6px;
        }
        .ci-coords {
          margin-top: 6px;
          font-size: 10px; color: #9CA3AF;
          text-align: center; font-weight: 500;
        }

        .ci-btns { padding-top: 12px; }
        .ci-btn-row { display: flex; gap: 10px; }

        .ci-btn-primary {
          flex: 1;
          background: #000;
          color: #fff;
          width: 320px; height: 40px;
          margin: 0 auto;
          border: none; border-radius: 30px;
          padding: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px; font-weight: 700; letter-spacing: 0.4px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: opacity 0.2s, transform 0.15s;
        }
        .ci-btn-primary:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
        .ci-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
        .ci-btn-primary.green {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          box-shadow: 0 4px 14px rgba(5,150,105,0.35);
        }

        .ci-btn-secondary {
          flex: 1;
          background: #F2F2F2;
          color: #333;
          border: 1px solid #E0E0E0; border-radius: 30px;
          padding: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 14px; font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          transition: background 0.2s;
        }
        .ci-btn-secondary:hover { background: #E8E8E8; }

        .ci-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 999;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ci-spinner {
          width: 44px; height: 44px;
          border: 3.5px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.75s linear infinite;
        }

        canvas { display: none; }
      `}</style>

      <div className="ci-frame">
        <div className="ci-phone">
          <div className="ci-card">

            {/* Header */}
            <div className="ci-header">
              <div className="ci-title-pill">Check In</div>
              <button className="ci-close" onClick={() => navigate("/dashboard")}>
                <IconClose />
              </button>
            </div>

            {/* Camera / Photo */}
            <div className="ci-cam-box">
              {photo ? (
                <>
                  <img src={photo} alt="captured" />

                  {/* On-screen Watermark Stamp matching React Native layout */}
                  <div className="wm-overlay-screen">
                    <div className="wm-brand-row">
                      <div className="wm-brand-left">
                        <div className="wm-logo-wrap">
                          <img src={logo} alt="logo" className="wm-logo" />
                        </div>
                        <div>
                          <div className="wm-title-bold">DB Skills</div>
                          <div className="wm-title-sub">Attendance System</div>
                        </div>
                      </div>
                      <div className="wm-status-in">
                        ✓ CHECKED IN<span className="wm-time">{capturedTime || time}</span>
                      </div>
                    </div>
                    <div className="wm-separator" />
                    <div className="wm-meta-row">
                      <div className="wm-meta-text">📅 {formattedDate}</div>
                      {location && (
                        <div className="wm-meta-text">
                          📍 {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                        </div>
                      )}
                    </div>
                    {address && (
                      <div className="wm-meta-text">
                        🏠 {address}
                      </div>
                    )}
                  </div>

                  {/* Toast Banner */}
                  {downloadState === "success" && (
                    <div className="ci-toast-banner">
                      <span style={{ fontSize: "16px" }}>✅</span>
                      <div>
                        <div className="ci-toast-title">Photo Saved to Downloads!</div>
                        <div className="ci-toast-sub">📁 {downloadedFileName}</div>
                      </div>
                    </div>
                  )}

                  {/* Floating Action Buttons */}
                  <div className="ci-floating-btns">
                    <button
                      className={`ci-float-btn${downloadState === "success" ? " success" : ""}`}
                      onClick={downloadPhoto}
                      disabled={downloading || sharing}
                      title="Download photo"
                    >
                      {downloadState === "downloading" ? (
                        <div className="ci-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      ) : downloadState === "success" ? (
                        <IconCheck />
                      ) : (
                        <IconDownload />
                      )}
                    </button>

                    <button
                      className="ci-float-btn share"
                      onClick={sharePhoto}
                      disabled={sharing || downloading}
                      title="Share photo"
                    >
                      {sharing ? (
                        <div className="ci-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      ) : (
                        <IconShare />
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <video ref={videoRef} autoPlay playsInline muted />
                  {!camReady && !camError && (
                    <div className="ci-cam-error">
                      <div className="ci-spinner" style={{ borderTopColor: "#7B8DFF" }} />
                      <span>Starting camera…</span>
                    </div>
                  )}
                  {camError && (
                    <div className="ci-cam-error">
                      <IconCamera />
                      <span>{camError}</span>
                    </div>
                  )}
                  {/* Flip button */}
                  {camReady && (
                    <button className="ci-flip-btn" onClick={() => setFacingFront(f => !f)} title="Flip camera">
                      <IconFlip />
                    </button>
                  )}
                </>
              )}

              {/* Shutter flash */}
              <div className={`ci-cam-flash${flash ? " on" : ""}`} />

              {/* Time pill */}
              {!photo && (
                <div className="ci-time-pill">
                  <IconTime />
                  {time}
                </div>
              )}
            </div>

            {/* Location card */}
            <div className="ci-loc-card">
              <div className="ci-loc-header">
                <IconPin />
                Current Location
              </div>
              <div className="ci-map-box">
                {mapSrc ? (
                  <iframe src={mapSrc} title="location" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                ) : (
                  <div className="ci-map-loading">
                    <div className="ci-spinner" style={{ width: 18, height: 18, borderWidth: 2, borderTopColor: "#7B8DFF" }} />
                    {locError || "Fetching location…"}
                  </div>
                )}
              </div>
              {location && (
                <p className="ci-coords">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="ci-btns">
              {!photo ? (
                <button
                  className="ci-btn-primary"
                  onClick={takePhoto}
                  disabled={!camReady || !!camError}
                >
                  <IconCamera />
                  Take Photo
                </button>
              ) : (
                <div className="ci-btn-row">
                  <button className="ci-btn-secondary" onClick={retake}>
                    <IconRetake />
                    Retake
                  </button>
                  <button
                    className="ci-btn-primary green"
                    style={{ flex: 2 }}
                    onClick={confirmCheckIn}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="ci-spinner" style={{ width: 20, height: 20, borderWidth: 2.5 }} />
                    ) : (
                      <>
                        <IconCheck />
                        Confirm Check In
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} />

      {/* Full-screen loading overlay */}
      {loading && (
        <div className="ci-overlay">
          <div className="ci-spinner" />
        </div>
      )}
    </>
  );
}